/**
 * HomePage — App-Desktop mit konfigurierbaren Modul-Kacheln + Mein Tag.
 *
 * Kacheln werden aus der DB geladen (home_kacheln), Icons via Lucide.
 * Darunter Handlungsbedarf (Action Items) + Aktivitäts-Feed.
 *
 * @page /
 * @roles alle (rollenbasiert gefiltert)
 * @api GET /api/home/kacheln, GET /api/home/badges, GET /api/home/action-items, GET /api/home/activity-feed
 * @since v2.278.0, redesign v2.281.0, lucide v2.281.1, configurable v2.282.0
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Package, Users, Calendar, UserPlus, BarChart3, PieChart,
  Building2, ClipboardList, Warehouse, Headphones, LayoutDashboard, Settings,
  ChevronRight, CheckCircle2, ClipboardCheck, Activity,
  CalendarOff, ListChecks, Clock, CalendarCheck, CalendarX2,
  Bell, FileText, TrendingUp, Home,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function getGreeting(t) {
  const h = new Date().getHours();
  if (h < 12) return t('home.greeting.morning');
  if (h < 18) return t('home.greeting.afternoon');
  return t('home.greeting.evening');
}

function formatDate(locale) {
  const loc = locale === 'de-DE' ? 'de-AT' : locale;
  return new Date().toLocaleDateString(loc, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Icon name → Lucide component map ──
const ICON_MAP = {
  Package, Users, Calendar, UserPlus, BarChart3, PieChart,
  Building2, ClipboardList, Warehouse, Headphones, LayoutDashboard, Settings,
  TrendingUp,
};

// ── Color name → Tailwind classes ──
const COLOR_MAP = {
  blue:    { bg: 'bg-blue-100',   text: 'text-blue-600' },
  purple:  { bg: 'bg-purple-100', text: 'text-purple-600' },
  indigo:  { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  green:   { bg: 'bg-green-100',  text: 'text-green-600' },
  emerald: { bg: 'bg-emerald-100',text: 'text-emerald-600' },
  cyan:    { bg: 'bg-cyan-100',   text: 'text-cyan-600' },
  amber:   { bg: 'bg-amber-100',  text: 'text-amber-600' },
  teal:    { bg: 'bg-teal-100',   text: 'text-teal-600' },
  orange:  { bg: 'bg-orange-100', text: 'text-orange-600' },
  slate:   { bg: 'bg-slate-100',  text: 'text-slate-600' },
  pink:    { bg: 'bg-pink-100',   text: 'text-pink-600' },
  sky:     { bg: 'bg-sky-100',    text: 'text-sky-600' },
  gray:    { bg: 'bg-gray-100',   text: 'text-gray-600' },
};

// ── Group icon mapping (Lucide) ──
const GROUP_ICONS = {
  RECRUITING:      UserPlus,
  CASES:           Headphones,
  ABWESENHEITEN:   CalendarOff,
  PERSONAL:        Users,
  AUFGABEN:        ListChecks,
  'MEINE AUFGABEN': ListChecks,
};

// ── Action Item definitions ──
const ACTION_DEFS = [
  { key: 'neue_bewerbungen',           group: 'RECRUITING',     labelKey: 'homePage.action.neueBewerbungen',   route: '/recruiting/bewerber' },
  { key: 'vertraege_offen',            group: 'RECRUITING',     labelKey: 'homePage.action.vertraegeOffen',    route: '/recruiting/bewerber' },
  { key: 'cases_sla_ueberschritten',   group: 'CASES',          labelKey: 'homePage.action.casesSla',          route: '/cases/liste' },
  { key: 'cases_ohne_zuweisung',       group: 'CASES',          labelKey: 'homePage.action.casesUnassigned',   route: '/cases/liste' },
  { key: 'urlaub_antraege_offen',      group: 'ABWESENHEITEN',  labelKey: 'homePage.action.urlaubOffen',       route: '/urlaub/antraege' },
  { key: 'aufgaben_offen',             group: 'AUFGABEN',       labelKey: 'homePage.action.aufgabenOffen',     route: '/aufgaben' },
  { key: 'aufgaben_ueberfaellig',      group: 'AUFGABEN',       labelKey: 'homePage.action.aufgabenOverdue',   route: '/aufgaben' },
  { key: 'onboarding_offen',           group: 'AUFGABEN',       labelKey: 'homePage.action.onboardingOffen',   route: '/mitarbeiter' },
  { key: 'meine_tasks_ueberfaellig',   group: 'MEINE AUFGABEN', labelKey: 'homePage.action.meineTasksOverdue', route: '/meine-aufgaben' },
  { key: 'meine_tasks_heute',          group: 'MEINE AUFGABEN', labelKey: 'homePage.action.meineTasksToday',   route: '/meine-aufgaben' },
  { key: 'meine_tasks_inbox',          group: 'MEINE AUFGABEN', labelKey: 'homePage.action.meineTasksInbox',   route: '/meine-aufgaben' },
  { key: 'ma_ohne_lohnkosten',         group: 'PERSONAL',       labelKey: 'homePage.action.maOhneLohnkosten',  route: '/mitarbeiter' },
  { key: 'lohnzettel_nicht_versendet', group: 'PERSONAL',       labelKey: 'homePage.action.lohnzettelOffen',   route: '/lohnverrechnung' },
];

// ── Feed Item component ──
function FeedItem({ item, t, locale }) {
  const ts = new Date(item.ts);
  const now = new Date();
  const isToday = ts.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = ts.toDateString() === yesterday.toDateString();

  let timeStr;
  if (isToday) {
    timeStr = `${t('homePage.feed.today')}, ${ts.toLocaleTimeString(locale === 'de-DE' ? 'de-AT' : locale, { hour: '2-digit', minute: '2-digit' })}`;
  } else if (isYesterday) {
    timeStr = t('homePage.feed.yesterday');
  } else {
    timeStr = ts.toLocaleDateString(locale === 'de-DE' ? 'de-AT' : locale, { day: '2-digit', month: '2-digit' });
  }

  let IconComp, iconClass, text;
  switch (item.type) {
    case 'stempel':
      IconComp = Clock;
      iconClass = 'text-[var(--text-tertiary)]';
      text = `${item.name} ${t(`homePage.feed.stempel.${item.detail}`) || item.detail}`;
      break;
    case 'bewerbung':
      IconComp = UserPlus;
      iconClass = 'text-green-500';
      text = `${t('homePage.feed.bewerbung')}: ${item.name}${item.detail ? ` (${item.detail})` : ''}`;
      break;
    case 'urlaub':
      IconComp = item.detail === 'genehmigt' ? CalendarCheck : CalendarX2;
      iconClass = item.detail === 'genehmigt' ? 'text-green-500' : 'text-red-400';
      text = `${t(`homePage.feed.urlaub.${item.detail}`)} — ${item.name}`;
      break;
    case 'notification':
      IconComp = Bell;
      iconClass = 'text-amber-500';
      text = item.name;
      break;
    default:
      IconComp = FileText;
      iconClass = 'text-[var(--text-tertiary)]';
      text = item.name || item.detail;
  }

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-[var(--border-table,#F3F4F6)] last:border-b-0">
      <span className="text-[11px] text-[var(--text-tertiary)] w-[100px] flex-shrink-0 pt-0.5 tabular-nums">{timeStr}</span>
      <IconComp size={15} className={`flex-shrink-0 mt-0.5 ${iconClass}`} />
      <span className="text-sm text-[var(--text-primary)] flex-1 leading-snug">{text}</span>
    </div>
  );
}

// ── Main Component ──
export default function HomePage() {
  const { user } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const [tiles, setTiles] = useState(null);
  const [badges, setBadges] = useState({});
  const [actionItems, setActionItems] = useState(null);
  const [activityFeed, setActivityFeed] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/home/kacheln`, { credentials: 'include' }).then(r => r.ok ? r.json() : { kacheln: [] }),
      fetch(`${API}/api/home/badges`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}),
      fetch(`${API}/api/home/action-items`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}),
      fetch(`${API}/api/home/activity-feed?limit=15`, { credentials: 'include' }).then(r => r.ok ? r.json() : { items: [] }),
    ]).then(([k, b, a, f]) => {
      setTiles(k.kacheln || []);
      setBadges(b);
      setActionItems(a);
      setActivityFeed(f.items || []);
    }).catch(() => {});
  }, []);

  const permissions = user?.permissions || {};
  const visibleTiles = (tiles || []).filter(t => {
    if (!t.modul) return true;
    const p = permissions[t.modul];
    return p && p !== 'none';
  });

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || '';

  // Group action items by category
  const activeItems = ACTION_DEFS.filter(ai => actionItems && actionItems[ai.key] > 0);
  const groupedItems = {};
  activeItems.forEach(ai => {
    if (!groupedItems[ai.group]) groupedItems[ai.group] = [];
    groupedItems[ai.group].push({ ...ai, count: actionItems[ai.key] });
  });

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Greeting */}
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-blue-50">
            <Home size={20} className="text-[#338edc]" />
          </div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            {getGreeting(t)}, {firstName}
          </h1>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{formatDate(locale)}</p>
      </div>

      {/* Module Grid — square tiles with accent colors */}
      {tiles === null ? (
        <div className="h-40 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[var(--border-default)] border-t-[#338edc] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-4 lg:grid-cols-6 gap-4">
          {visibleTiles.map(tile => {
            const Icon = ICON_MAP[tile.icon] || Package;
            const badge = tile.badge_key ? badges[tile.badge_key] : null;
            const color = COLOR_MAP[tile.farbe] || COLOR_MAP.blue;
            const label = locale === 'de-DE' ? tile.name_de : tile.name_en;
            return (
              <div
                key={tile.key}
                onClick={() => navigate(tile.route)}
                className="aspect-square border border-[var(--border-default,#E5E7EB)] rounded-2xl p-4 hover:border-[#338edc] cursor-pointer transition-all group bg-[var(--bg-card,#fff)] flex flex-col items-center justify-center text-center"
              >
                <div className={`w-14 h-14 rounded-xl ${color.bg} flex items-center justify-center`}>
                  <Icon size={28} className={color.text} />
                </div>
                <span className="text-sm font-medium text-[var(--text-primary)] mt-3">{label}</span>
                <div className="h-6 flex items-center mt-1">
                  {badge > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">{badge}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mein Tag — Action Items + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Handlungsbedarf */}
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-2xl bg-[var(--bg-card,#fff)] p-6">
          <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <ClipboardCheck size={18} className="text-[#F59E0B]" />
            {t('homePage.actionItems')}
          </h2>
          {actionItems === null ? (
            <div className="h-20 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-[var(--border-default)] border-t-[#338edc] rounded-full animate-spin" />
            </div>
          ) : activeItems.length === 0 ? (
            <div className="text-sm text-[var(--text-secondary)] flex items-center gap-2 py-4">
              <CheckCircle2 size={18} className="text-green-500" />
              {t('homePage.allDone')}
            </div>
          ) : (
            Object.entries(groupedItems).map(([group, items]) => {
              const GroupIcon = GROUP_ICONS[group] || ClipboardList;
              return (
                <div key={group} className="mb-4 last:mb-0">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5 flex items-center gap-1.5">
                    <GroupIcon size={13} />
                    {group}
                  </div>
                  {items.map(item => (
                    <div
                      key={item.key}
                      onClick={() => navigate(item.route)}
                      className="flex items-center justify-between py-2 px-3 -mx-1 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors cursor-pointer group/item"
                    >
                      <span className="text-sm text-[var(--text-primary)]">
                        <span className="font-semibold">{item.count}</span> {t(item.labelKey)}
                      </span>
                      <span className="text-xs text-[#338edc] flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
                        {t('homePage.view')} <ChevronRight size={14} />
                      </span>
                    </div>
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* Aktivitäts-Feed */}
        <div className="border border-[var(--border-default,#E5E7EB)] rounded-2xl bg-[var(--bg-card,#fff)] p-6">
          <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
            <Activity size={18} className="text-[#338edc]" />
            {t('homePage.activityFeed')}
          </h2>
          {activityFeed === null ? (
            <div className="h-20 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-[var(--border-default)] border-t-[#338edc] rounded-full animate-spin" />
            </div>
          ) : activityFeed.length > 0 ? (
            <div>
              {activityFeed.map((item, i) => (
                <FeedItem key={i} item={item} t={t} locale={locale} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-[var(--text-tertiary)] py-4">{t('homePage.noActivity')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
